import { FilterFilled } from "@ant-design/icons";
import { Switch } from "antd";
import { ColumnsType } from "antd/es/table";
import classNames from "classnames";
import {
  TableButton,
  TableSearchFilter,
  RangeSlider,
  DateRange,
  TableRadioGroup,
} from "shared/ui";
import { StandaloneTask } from "shared/api";

interface UseTaskHubStandAloneColumnsProps {
  filters: ReturnType<
    typeof import("shared/hooks/useTaskHubFilters").useTaskHubFilters
  >;
  serversOptions: { label: string; value: number }[];
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onToggleStatus: (task: StandaloneTask) => void;
}

export const useTaskHubStandAloneColumns = ({
  filters,
  serversOptions,
  onEdit,
  onDelete,
  onToggleStatus,
}: UseTaskHubStandAloneColumnsProps): ColumnsType<StandaloneTask> => {
  const {
    standAloneNameValue,
    handleStandAloneNameValue,
    clearStandAloneNameValue,

    serverFilter,
    setServerFilter,

    fieldValue,
    handleFieldValue,
    clearFieldValue,

    measurementValue,
    handleMeasurementValue,
    clearMeasurementValue,

    lookbackRange,
    priorityRange,
    targetRange,
    minSoftRange,
    maxSoftRange,
    minHardRange,
    maxHardRange,
    lastTimestampFilters,
    enabledFilter,
    setEnabledFilter,
  } = filters;

  return [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-blue-600 scale-125": !!standAloneNameValue,
          })}
        />
      ),
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={clearStandAloneNameValue}
          confirm={confirm}
          value={standAloneNameValue}
          handleFilterChange={handleStandAloneNameValue}
          visible={visible}
          placeholder="Search by name"
          title="Name"
        />
      ),
    },
    {
      title: "Server",
      dataIndex: "influxdb_server",
      key: "influxdb_server",
      render: (id: number) =>
        serversOptions.find((s) => s.value === id)?.label ?? id,
      filterDropdown: ({ confirm }) => (
        <TableRadioGroup
          options={serversOptions}
          selectedValue={serverFilter ?? ""}
          onChange={(val) => {
            setServerFilter(val as number);
            confirm();
          }}
        />
      ),
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-blue-600 scale-125": serverFilter !== undefined,
          })}
        />
      ),
    },
    {
      title: "Field",
      dataIndex: "field",
      key: "field",
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-blue-600 scale-125": !!fieldValue,
          })}
        />
      ),
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={clearFieldValue}
          confirm={confirm}
          value={fieldValue}
          handleFilterChange={handleFieldValue}
          visible={visible}
          placeholder="Search by field"
          title="Field"
        />
      ),
    },
    {
      title: "Measurement",
      dataIndex: "measurement",
      key: "measurement",
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-blue-600 scale-125": !!measurementValue,
          })}
        />
      ),
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={clearMeasurementValue}
          confirm={confirm}
          value={measurementValue}
          handleFilterChange={handleMeasurementValue}
          visible={visible}
          placeholder="Search by measurement"
          title="Measurement"
        />
      ),
    },
    {
      title: "Lookback Days",
      dataIndex: "lookback_days",
      key: "lookback_days",
      filterDropdown: () => (
        <RangeSlider
          title="Lookback Days"
          onChange={lookbackRange.onChange}
          onAfterChange={lookbackRange.onAfterChange}
          rangeItemName="Days"
        />
      ),
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-green-600 scale-125": lookbackRange.isFilterActive(),
          })}
        />
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      filterDropdown: () => (
        <RangeSlider
          title="Priority"
          maxValue={5}
          step={1}
          onChange={priorityRange.onChange}
          onAfterChange={priorityRange.onAfterChange}
          rangeItemName="Priority"
        />
      ),
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-yellow-600 scale-125": priorityRange.isFilterActive(),
          })}
        />
      ),
    },
    {
      title: "Target Value",
      dataIndex: "target_value",
      key: "target_value",
      filterDropdown: () => (
        <RangeSlider
          title="Target Value"
          onChange={targetRange.onChange}
          onAfterChange={targetRange.onAfterChange}
          rangeItemName="Target"
        />
      ),
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-yellow-500 scale-125": targetRange.isFilterActive(),
          })}
        />
      ),
    },
    {
      title: "Min Soft",
      dataIndex: "min_soft",
      key: "min_soft",
      filterDropdown: () => (
        <RangeSlider
          title="Min Soft"
          onChange={minSoftRange.onChange}
          onAfterChange={minSoftRange.onAfterChange}
          rangeItemName="Min Soft"
        />
      ),
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-blue-400 scale-125": minSoftRange.isFilterActive(),
          })}
        />
      ),
    },
    {
      title: "Max Soft",
      dataIndex: "max_soft",
      key: "max_soft",
      filterDropdown: () => (
        <RangeSlider
          title="Max Soft"
          onChange={maxSoftRange.onChange}
          onAfterChange={maxSoftRange.onAfterChange}
          rangeItemName="Max Soft"
        />
      ),
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-blue-400 scale-125": maxSoftRange.isFilterActive(),
          })}
        />
      ),
    },
    {
      title: "Min Hard",
      dataIndex: "min_hard",
      key: "min_hard",
      filterDropdown: () => (
        <RangeSlider
          title="Min Hard"
          onChange={minHardRange.onChange}
          onAfterChange={minHardRange.onAfterChange}
          rangeItemName="Min Hard"
        />
      ),
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-purple-400 scale-125": minHardRange.isFilterActive(),
          })}
        />
      ),
    },
    {
      title: "Max Hard",
      dataIndex: "max_hard",
      key: "max_hard",
      filterDropdown: () => (
        <RangeSlider
          title="Max Hard"
          onChange={maxHardRange.onChange}
          onAfterChange={maxHardRange.onAfterChange}
          rangeItemName="Max Hard"
        />
      ),
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-purple-400 scale-125": maxHardRange.isFilterActive(),
          })}
        />
      ),
    },
    {
      title: "Last Timestamp",
      dataIndex: "last_timestamp_with_data",
      key: "last_timestamp_with_data",
      filterDropdown: () => (
        <DateRange
          title="Last Timestamp"
          leftPickerTitle="From"
          rightPickerTitle="To"
          handlePickerChangeGreater={lastTimestampFilters.handleDateGreater}
          handlePickerChangeLesser={lastTimestampFilters.handleDateLesser}
        />
      ),
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-green-600 scale-125":
              lastTimestampFilters.isDataFilterActive,
          })}
        />
      ),
    },
    {
      title: "Enabled",
      key: "enabled",
      dataIndex: "enabled",
      filterDropdown: ({ confirm }) => {
        const getSelectedValue = () => {
          if (enabledFilter === undefined) return "all";
          if (enabledFilter) return "true";
          return "false";
        };

        return (
          <TableRadioGroup
            options={[
              { label: "All", value: "all" },
              { label: "Enabled", value: "true" },
              { label: "Disabled", value: "false" },
            ]}
            selectedValue={getSelectedValue()}
            onChange={(val) => {
              const parsedValue =
                /* eslint-disable-next-line no-nested-ternary */
                val === "true" ? true : val === "false" ? false : undefined;

              setEnabledFilter(parsedValue);
              confirm();
            }}
          />
        );
      },
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-green-600 scale-125": enabledFilter !== undefined,
          })}
        />
      ),
      render: (_: boolean, task: StandaloneTask) => (
        <Switch
          checked={task.enabled}
          onChange={(_, event) => {
            event?.stopPropagation();
            onToggleStatus(task);
          }}
          style={{
            backgroundColor: task.enabled ? "limegreen" : "tomato",
          }}
        />
      ),
    },

    {
      title: "Actions",
      key: "actions",
      render: (task: StandaloneTask) => (
        <div
          className="flex gap-2"
          role="button"
          tabIndex={0}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") e.stopPropagation();
          }}
        >
          <TableButton type="edit" onClick={() => onEdit(task.id)} />
          <TableButton type="delete" onClick={() => onDelete(task.id)} />
        </div>
      ),
    },
  ];
};
